import { createTheme } from '@mui/material/styles'

/**
 * Banner tones (Design System, Banners). The tone marks the icon, border,
 * fill and actions; the message itself is always ivory. Warning is amber, not
 * the brand gold, so a banner is never mistaken for the screen's primary action.
 */
export const bannerTones = {
  info: '122 184 245',
  success: '111 207 151',
  warning: '242 166 90',
  error: '239 83 80',
} as const
export type BannerTone = keyof typeof bannerTones
const toneOf = (severity: unknown): string => bannerTones[(typeof severity === 'string' && severity in bannerTones ? severity : 'success') as BannerTone]

export const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: '#e8b84b', contrastText: '#0a1628' },
    secondary: { main: '#162e4a', contrastText: '#f2efe4' },
    background: { default: '#0a1628', paper: '#112240' },
    text: { primary: '#f2efe4', secondary: '#7a90a8' },
    divider: '#3a5068',
    error: { main: '#ef5350' },
  },
  shape: { borderRadius: 12 },
  spacing: 8,
  typography: {
    fontFamily:
      'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    button: { textTransform: 'none', fontWeight: 700 },
  },
  components: {
    MuiButton: {
      defaultProps: { size: 'large' },
      styleOverrides: {
        root: {
          borderRadius: 10,
          // R5 — gold marks the screen's single primary action. Text and
          // outlined actions stay ivory on slate so no screen competes. MUI
          // emits variant and colour as two classes, so both are required to
          // out-specify the palette-derived rule.
          '&.MuiButton-text.MuiButton-colorPrimary': { color: '#f2efe4' },
          '&.MuiButton-text.MuiButton-colorPrimary:hover': {
            backgroundColor: 'rgb(242 239 228 / 8%)',
            color: '#f2efe4',
          },
          '&.MuiButton-outlined.MuiButton-colorPrimary': {
            color: '#f2efe4',
            borderColor: 'rgb(122 144 168 / 35%)',
          },
          '&.MuiButton-outlined.MuiButton-colorPrimary:hover': {
            backgroundColor: 'rgb(242 239 228 / 6%)',
            borderColor: 'rgb(122 144 168 / 65%)',
            color: '#f2efe4',
          },
          '&.MuiButton-outlined.MuiButton-colorPrimary.Mui-disabled': {
            borderColor: 'rgb(122 144 168 / 25%)',
            color: 'rgb(242 239 228 / 38%)',
          },
        },
        contained: {
          boxShadow: '0 4px 12px rgb(232 184 75 / 28%)',
          '&.Mui-disabled': {
            color: '#0a1628',
            backgroundColor: '#e8b84b',
            opacity: 0.35,
          },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: '#f2efe4',
          textDecorationColor: 'rgb(122 144 168 / 70%)',
          '&:hover, &:focus-visible': { textDecorationColor: '#f2efe4' },
          '&.MuiLink-colorPrimary': { color: '#f2efe4' },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          border: '1px solid rgb(122 144 168 / 22%)',
          backgroundImage: 'none',
          boxShadow: '0 18px 48px rgb(3 7 18 / 28%)',
        },
      },
    },
    // MUI paints dark-mode Paper with a light overlay so a surface separates
    // from the background behind it. MuiCard already strips that overlay and
    // takes the app's border instead; without the same override a dialog reads
    // as a different, paler material from the card it sits on top of.
    MuiDialog: {
      styleOverrides: {
        paper: {
          border: '1px solid rgb(122 144 168 / 22%)',
          backgroundImage: 'none',
        },
      },
    },
    // One set of paddings for every dialog, and room in the title for the
    // shared close control rather than a bespoke margin in each screen.
    MuiDialogTitle: { styleOverrides: { root: { position: 'relative', padding: '20px 24px 8px', paddingRight: 52 } } },
    MuiDialogContent: { styleOverrides: { root: { padding: '12px 24px' } } },
    MuiDialogActions: { styleOverrides: { root: { padding: '12px 24px 20px' } } },
    // One banner surface for every alert. MUI's stock dark alert is a
    // near-black fill with the message tinted by severity; here the tone only
    // tints the fill, border, icon and actions, and the message stays ivory.
    MuiAlert: {
      styleOverrides: {
        root: ({ ownerState }) => {
          const tone = toneOf(ownerState.severity)
          return {
            flexWrap: ownerState.action ? 'wrap' : 'nowrap',
            padding: '6px 12px 6px 14px',
            border: `1px solid rgb(${tone} / 38%)`,
            borderRadius: 14,
            backgroundColor: `rgb(${tone} / 10%)`,
            color: '#f2efe4',
            fontSize: '0.9375rem',
            lineHeight: 1.45,
          }
        },
        icon: ({ ownerState }) => ({ color: `rgb(${toneOf(ownerState.severity)})`, opacity: 1 }),
        message: { minWidth: 0, flex: '1 1 0', color: '#f2efe4' },
        action: ({ ownerState }) => {
          const tone = `rgb(${toneOf(ownerState.severity)})`
          // Text buttons in a banner take its tone, never gold or ivory. The
          // extra classes out-specify the R5 rule on text primary buttons.
          const toned = {
            '& .MuiButton-root.MuiButton-text.MuiButton-colorPrimary, & .MuiButton-root.MuiButton-text.MuiButton-colorInherit': { color: tone },
            '& .MuiIconButton-root': { color: '#7a90a8' },
          }
          // An action (Try again, Review…) goes on its own row under the
          // message, lined up with the text, so long messages never squeeze it
          // on a phone. A close-only alert keeps its ✕ at the top right.
          return ownerState.action
            ? { ...toned, flexBasis: '100%', margin: '-6px 0 2px 26px', padding: 0, gap: 4, justifyContent: 'flex-start' }
            : { ...toned, alignSelf: 'flex-start', paddingTop: 2 }
        },
      },
    },
    MuiTextField: { defaultProps: { fullWidth: true, size: 'medium' } },
  },
})
